import { formatNumber } from "./format.ts";
import type { MessageKey, MessageVars, PluralKey } from "./keys.ts";
import { DEFAULT_LOCALE, type Locale } from "./locales.ts";
import { bcp47 } from "./resolve.ts";

/**
 * `t` and `plural`, built over whichever areas of the catalog a bundle actually needs.
 *
 * A factory rather than one global `t`, because the booking island renders `booking.*` and
 * `manage.*` and nothing else, and shipping the settings screens' 14 KB of Swedish to a
 * customer's phone costs a tenth of the island's entire budget (AGENT_PROMPTS.md P8).
 * `catalogs/*.ts` name the audiences; this is the machinery under them.
 */

export type AreaCatalog = Readonly<Record<string, string>>;

export interface Translator {
  t(key: MessageKey, locale?: Locale, vars?: MessageVars): string;
  plural(key: PluralKey, count: number, locale?: Locale, vars?: MessageVars): string;
}

/**
 * Merge the areas of one locale into one lookup.
 *
 * Spread rather than a nested structure: keys are full and dotted in the files, so a flat
 * record is what the files already describe and a lookup is one property access.
 */
export function mergeAreas(areas: readonly AreaCatalog[]): AreaCatalog {
  return Object.assign({}, ...areas) as AreaCatalog;
}

export function createTranslator(
  catalogs: Readonly<Record<Locale, AreaCatalog>>,
  /** The locale every lookup falls through to before giving up. `en` everywhere. */
  fallback: Locale = "en",
): Translator {
  /**
   * Resolve a message. Falls back locale → en → sv → the key itself. Returning the key
   * rather than throwing is deliberate: a missing translation should be visible in the UI
   * and caught in review, not take down the page for a customer mid-booking.
   *
   * `{placeholder}` substitution is deliberately this small. It is not a template language:
   * there are no expressions, no conditionals and no formatting, because a translator
   * editing a sentence should not be able to break a render. Dates and money arrive already
   * formatted, by code that knows the timezone and the currency (CLAUDE.md), and an
   * unmatched placeholder is left in place so it shows up in a preview rather than vanishing
   * silently.
   */
  function t(key: MessageKey, locale: Locale = DEFAULT_LOCALE, vars?: MessageVars): string {
    const message =
      catalogs[locale][key] ?? catalogs[fallback][key] ?? catalogs[DEFAULT_LOCALE][key] ?? key;
    if (vars === undefined) return message;

    return message.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
      Object.hasOwn(vars, name) ? (vars[name] as string) : placeholder,
    );
  }

  /**
   * A counted message, in the plural form the language actually has.
   *
   * The catalog holds one key per CLDR category — `network.subtitle.one`,
   * `network.subtitle.other` — and `Intl.PluralRules` picks between them. Swedish, Norwegian
   * and English all have two forms, so this looks like overkill for the three shipped
   * languages; it is not, because `count === 1 ? a : b` at the call site is what makes adding
   * a language with more forms a change to every screen rather than to a catalog.
   *
   * `{count}` is substituted already formatted, so a Swedish reader gets `1 250` and not
   * `1250`. Falls back to `.other` when a category is missing, which is what makes a catalog
   * that only bothered with two forms still render.
   */
  function plural(
    key: PluralKey,
    count: number,
    locale: Locale = DEFAULT_LOCALE,
    vars?: MessageVars,
  ): string {
    const category = new Intl.PluralRules(bcp47(locale)).select(count);
    const exact = `${key}.${category}` as MessageKey;
    const resolved = t(exact, locale) === exact ? (`${key}.other` as MessageKey) : exact;

    return t(resolved, locale, { ...vars, count: formatNumber(count, locale) });
  }

  return { t, plural };
}
